
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import {
  Users, FileBox, Download, Clock, Calendar
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCollections: 0,
    totalDownloads: {
      success: 0,
      failed: 0
    },
    recentActivity: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    async function fetchDashboardData() {
      setIsLoading(true);
      
      try {
        const [usersCount, collectionsCount, downloadsData, dailyStats] = await Promise.all([
          // Get total users count
          supabase
            .from('users')
            .select('id', { count: 'exact', head: true }),
          
          // Get total collections count
          supabase
            .from('collections')
            .select('id', { count: 'exact', head: true }),
          
          // Get download attempts counts
          supabase
            .from('download_attempts')
            .select('status'),
          
          // Get daily stats for chart (last 7 days)
          supabase
            .from('download_attempts')
            .select('created_at, status')
            .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        ]);

        // Count successful and failed downloads
        const successCount = downloadsData.data?.filter(d => d.status === 'success').length || 0;
        const failedCount = downloadsData.data?.filter(d => d.status === 'failed').length || 0;
        
        // Process chart data
        const dailyData = processChartData(dailyStats.data || []);
        
        setStats({
          totalUsers: usersCount.count || 0,
          totalCollections: collectionsCount.count || 0,
          totalDownloads: {
            success: successCount,
            failed: failedCount
          },
          recentActivity: []
        });
        
        setChartData(dailyData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  // Process data for chart visualization
  function processChartData(data: any[]) {
    const days = new Map();
    
    // Get last 7 days including today
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      days.set(dateStr, { date: dateStr, successful: 0, failed: 0 });
    }
    
    // Populate with actual data
    data.forEach(item => {
      const dateStr = new Date(item.created_at).toISOString().split('T')[0];
      if (days.has(dateStr)) {
        const day = days.get(dateStr);
        if (item.status === 'success') {
          day.successful += 1;
        } else {
          day.failed += 1;
        }
      }
    });
    
    return Array.from(days.values());
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-gray-400 mt-1">Overview of your TikTok collection data</p>
        </div>
        <div className="flex items-center mt-4 md:mt-0 space-x-2">
          <Clock className="h-5 w-5 text-gray-400" />
          <span className="text-gray-400">Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Stat cards row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-[#1A1F2C] border-gray-800/50 p-6 rounded-xl">
          <div className="flex items-start">
            <div className="p-3 rounded-full bg-blue-500/10 mr-4">
              <Users className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">Total Users</p>
              <h3 className="text-2xl font-bold">{stats.totalUsers}</h3>
            </div>
          </div>
        </Card>

        <Card className="bg-[#1A1F2C] border-gray-800/50 p-6 rounded-xl">
          <div className="flex items-start">
            <div className="p-3 rounded-full bg-purple-500/10 mr-4">
              <FileBox className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">Collections</p>
              <h3 className="text-2xl font-bold">{stats.totalCollections}</h3>
            </div>
          </div>
        </Card>

        <Card className="bg-[#1A1F2C] border-gray-800/50 p-6 rounded-xl">
          <div className="flex items-start">
            <div className="p-3 rounded-full bg-green-500/10 mr-4">
              <Download className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">Successful Downloads</p>
              <h3 className="text-2xl font-bold">{stats.totalDownloads.success}</h3>
            </div>
          </div>
        </Card>

        <Card className="bg-[#1A1F2C] border-gray-800/50 p-6 rounded-xl">
          <div className="flex items-start">
            <div className="p-3 rounded-full bg-red-500/10 mr-4">
              <Download className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">Failed Downloads</p>
              <h3 className="text-2xl font-bold">{stats.totalDownloads.failed}</h3>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-8">
        <Card className="bg-[#1A1F2C] border-gray-800/50 p-6 rounded-xl">
          <h3 className="text-lg font-semibold mb-4">Download Activity (Last 7 Days)</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis 
                  dataKey="date" 
                  stroke="#6b7280"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth()+1}/${date.getDate()}`;
                  }}
                />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', color: 'white' }}
                  labelStyle={{ color: 'white' }}
                />
                <Legend />
                <Bar dataKey="successful" fill="#10B981" name="Successful" />
                <Bar dataKey="failed" fill="#EF4444" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
